/**
 * BacklogPanel — replacement for the old "Demand by jurisdiction" rail.
 *
 * Two improvements the user called out explicitly:
 *   1. Stop fronting "No jurisdiction" for every site — group primarily by
 *      city, fold all 1-job locations into a single "Other locations" row
 *      so the rail isn't a wall of singletons.
 *   2. Adapt to the auto-scheduler:
 *        • collapsed = false → full-size manual mode. Group by city, expand
 *          a row to see jobs and one-click bulk assign.
 *        • collapsed = true  → slim summary card. The auto-scheduler is
 *          handling routine placement, so we only highlight jobs that
 *          *need a human* (missing tech skill, no city certification, etc.)
 *          and let the dispatcher click through to expand the full backlog.
 */
import { useCallback, useEffect, useMemo, useState, type DragEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    AlertTriangle,
    CalendarPlus,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    GripVertical,
    Inbox,
    Loader2,
    MapPin,
    Search,
    Sparkles,
    UserX,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { PageSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { TRAY_NOT_READY_ROW, TrayReadyTags } from "./new-grid/tray-ready";
import { classifyDueState } from "@/lib/dueUrgency";
import { isContactStale } from "@/lib/contactFreshness";
import type { Cluster, ClusterJob } from "./demand-panel";
import { formatJobAddress, type DispatchTech } from "./dispatch-types";

interface ClustersResponse {
    clusters: Cluster[];
    totalJobs: number;
}

const RETURN_REASON_LABEL: Record<string, string> = {
    no_access: "no access",
    manager_unavailable: "manager unavailable",
    water_shutoff: "water can't be shut off",
    no_water: "no water to device",
    ran_out_of_time: "out of time",
    parts_needed: "need parts",
    unsafe: "unsafe/blocked",
    other: "return needed",
    cannot_complete: "cannot complete",
};

/**
 * "↩ return visit" chip on the Awaiting Dispatch card — the return trip
 * carries its story: reason, which devices remain (passed devices never
 * re-enter the trip), a link to the original visit, and the original
 * due-by (deadline pressure survives the reschedule).
 */
function ReturnVisitChip({ context, jobId }: { context: NonNullable<ClusterJob["returnContext"]>; jobId: string }) {
    const reasons = (context.reasons ?? []).map((r) => RETURN_REASON_LABEL[r] ?? r).join(", ");
    const remaining = context.remaining_devices ?? [];
    const original = context.original_visit;
    const originalLabel = original?.scheduled_start
        ? new Date(original.scheduled_start).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;
    return (
        <span className="mt-0.5 flex w-fit flex-col gap-0.5">
            <span
                className="inline-flex w-fit items-center gap-1 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
                title={context.reason_text ? `Reason: ${context.reason_text}` : undefined}
            >
                ↩ return visit{reasons ? ` — ${reasons}` : ""}
            </span>
            {context.reason_text && (
                <span className="text-[10px] italic text-foreground/60">“{context.reason_text}”</span>
            )}
            {remaining.length > 0 && (
                <span
                    className="text-[10px] text-foreground/70"
                    title={remaining
                        .map((d) => [d.label, d.serial ? `S/N ${d.serial}` : null].filter(Boolean).join(" "))
                        .join("; ")}
                >
                    Remaining: {remaining.map((d) => d.label).join(", ")}
                </span>
            )}
            {originalLabel && (
                <Link
                    to={`/jobs/${jobId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] text-primary hover:underline"
                    title={original?.tech_name ? `Original visit with ${original.tech_name}` : "Original visit"}
                >
                    Original visit {originalLabel}
                    {original?.tech_name ? ` · ${original.tech_name}` : ""}
                </Link>
            )}
        </span>
    );
}

interface BacklogPanelProps {
    techs: DispatchTech[];
    /** #1766 — the new Grid: each card shows "Ready for the tech" (or what's
     *  missing) before it is dragged. Off on the classic Grid. */
    showReadiness?: boolean;
    /** Date used by "auto-assign group" bulk action. */
    targetDate: string;
    /**
     * When true, render the slim summary card mode (auto-scheduler is on).
     * When false, render the full grouped backlog mode.
     */
    collapsed: boolean;
    onJobClick?: (jobId: string) => void;
    /** Primary card action — opens the explicit Schedule dialog for the job.
     *  When provided, clicking a card title opens the dialog instead of only
     *  navigating to the job. */
    onScheduleJob?: (jobId: string) => void;
    onJobDragStart?: (jobId: string) => void;
    onJobDragEnd?: () => void;
    onUnscheduleJob?: (jobId: string) => void;
    /** H24: cancel a backlog job (opens the guarded cancel dialog in the shell). */
    onCancelJob?: (jobId: string) => void;
    /** H24: assign a backlog job to a tech without scheduling a time. */
    onAssignJob?: (jobId: string, techId: string) => void;

    /** Eligibility maps so we can flag jobs that need a human (no tech holds
     *  the required skill, no tech is certified for the city, etc.).
     *
     *  `techSkills` is shaped as `Map<userId, string[]>` — that's how
     *  `dispatch-shell.tsx` and `day-swimlane.tsx` already build it from the
     *  skills summary endpoint, so we keep the same shape here to avoid an
     *  extra Set conversion in the parent. */
    techSkills?: Map<string, string[]>;
    jobSkillReqs?: Map<string, string[]>;
    techCityCerts?: Map<string, Set<string>>;
    jobCityRestriction?: Map<string, { id: string; label: string }>;
}

const DEFAULT_VISIT_MINUTES = 60;
const BACKLOG_DENSITY_KEY = "dispatch:backlogDensity";

/** ISO date → M/D for the "not before" parts-floor chip. */
function formatNotBeforeLabel(date: string): string {
    const d = new Date(`${date}T00:00:00`);
    if (Number.isNaN(d.getTime())) return date;
    return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Backlog due-pill tier. The card's own three-tier scale (the shared
 * classifyDueState is a two-tier overdue/soon used for borders + flags):
 *   - "red"   → overdue or due within 7 days
 *   - "amber" → due within 21 days
 *   - "gray"  → further out (still shows the date so the dispatcher can plan)
 */
type DueTier = "red" | "amber" | "gray";

function backlogDueTier(dueByDate: string | null | undefined): DueTier | null {
    if (!dueByDate) return null;
    const due = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(due.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86_400_000);
    if (diffDays <= 7) return "red";
    if (diffDays <= 21) return "amber";
    return "gray";
}

function dueTierClass(tier: DueTier | null): string {
    switch (tier) {
        case "red":
            return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
        case "amber":
            return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
        default:
            return "bg-foreground/10 text-foreground/70";
    }
}

/** ISO date (YYYY-MM-DD) → "Due Jun 28" (local time, abbreviated month). */
function formatDueDatePill(dueByDate: string): string {
    const d = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return `Due ${dueByDate}`;
    return `Due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/** Group jobs by city; fold any single-job city into a synthetic group. */
interface BacklogGroup {
    key: string;
    label: string;
    region: string | null;
    jurisdiction: string | null;
    jobs: ClusterJob[];
    /** #629: jobs from this city already placed on the board (today or later)
     *  — with jobs.length (the remaining), yields "N of M placed". */
    scheduledCount: number;
    /** True for the synthetic "Other locations" rollup. */
    isRollup: boolean;
    /** G6.1 polish: the rollup's member city names, so the group header can
     *  list them ("Burleson · Azle · Keller…") and scan before opening. */
    cities?: string[];
}

interface Rebucketed {
    /** Cities with ≥2 jobs — collapsible groups with bulk-assign. */
    multi: BacklogGroup[];
    /** Every single-job city, flat and alphabetical. #587: these stay NAMED
     *  and findable instead of vanishing into an opaque "N cities" bucket, so
     *  a city worked down to its last job never has to be hunted for. */
    singles: ClusterJob[];
}

export function rebucketByCity(clusters: Cluster[]): Rebucketed {
    const cityMap = new Map<string, BacklogGroup>();
    for (const c of clusters) {
        const cityLabel = (c.city ?? "").trim() || "Unknown location";
        const key = cityLabel.toLowerCase();
        let group = cityMap.get(key);
        if (!group) {
            group = {
                key,
                label: cityLabel,
                region: null,
                jurisdiction: c.jurisdiction,
                jobs: [],
                scheduledCount: 0,
                isRollup: false,
            };
            cityMap.set(key, group);
        }
        group.jobs.push(...c.jobs);
        // #629: a city can span several server clusters (different
        // jurisdictions / ZIP prefixes) — the placed counts add up.
        group.scheduledCount += c.scheduledCount ?? 0;
        if (!group.jurisdiction && c.jurisdiction) group.jurisdiction = c.jurisdiction;
    }
    const all = Array.from(cityMap.values());
    const multi = all.filter((g) => g.jobs.length >= 2).sort((a, b) => b.jobs.length - a.jobs.length);
    const singles = all
        .filter((g) => g.jobs.length === 1)
        .flatMap((g) => g.jobs.map((j) => ({ ...j, city: j.city ?? g.label })))
        .sort((a, b) => (a.city ?? "").localeCompare(b.city ?? ""));
    return { multi, singles };
}

/** Sort jobs by hard deadline, soonest first, nulls last. */
function byDueDateAsc(a: ClusterJob, b: ClusterJob): number {
    const da = a.dueByDate ?? "";
    const db = b.dueByDate ?? "";
    if (da && db) return da.localeCompare(db);
    if (da) return -1;
    if (db) return 1;
    return (a.city ?? "").localeCompare(b.city ?? "");
}

/**
 * Returns jobs that need a human override:
 *   • required skill exists but no on-staff tech holds it
 *   • job sits in a restricted city no on-staff tech is certified for
 */
function diagnoseHumanReview(
    jobs: ClusterJob[],
    args: Pick<BacklogPanelProps, "techSkills" | "jobSkillReqs" | "techCityCerts" | "jobCityRestriction">,
): Array<{ job: ClusterJob; reasons: string[] }> {
    const techsHaveSkill = (skill: string): boolean => {
        if (!args.techSkills) return true;
        for (const codes of args.techSkills.values()) if (codes.includes(skill)) return true;
        return false;
    };
    const techsCovering = (cityId: string): boolean => {
        if (!args.techCityCerts) return false;
        for (const set of args.techCityCerts.values()) if (set.has(cityId)) return true;
        return false;
    };
    const flagged: Array<{ job: ClusterJob; reasons: string[] }> = [];
    for (const j of jobs) {
        const reasons: string[] = [];
        const reqs = args.jobSkillReqs?.get(j.id) ?? [];
        const missingSkills = reqs.filter((s) => !techsHaveSkill(s));
        if (missingSkills.length > 0) {
            reasons.push(`No tech holds ${missingSkills.join(", ")}`);
        }
        const cityRestriction = args.jobCityRestriction?.get(j.id);
        if (cityRestriction && !techsCovering(cityRestriction.id)) {
            reasons.push(`No tech certified for ${cityRestriction.label}`);
        }
        if (reasons.length > 0) flagged.push({ job: j, reasons });
    }
    return flagged;
}

export function BacklogPanel({
    techs,
    showReadiness = false,
    targetDate,
    collapsed,
    onJobClick,
    onScheduleJob,
    onJobDragStart,
    onJobDragEnd,
    onUnscheduleJob,
    onCancelJob,
    onAssignJob,
    techSkills,
    jobSkillReqs,
    techCityCerts,
    jobCityRestriction,
}: BacklogPanelProps) {
    const qc = useQueryClient();
    const { can } = useAuth();
    const canAutomate = can(PERMISSIONS.dispatch.manage);
    const navigate = useNavigate();
    const clustersQuery = useQuery({
        queryKey: ["dispatch", "clusters"],
        queryFn: () => api.get<ClustersResponse>("/api/dispatch/clusters"),
    });

    const [forceExpanded, setForceExpanded] = useState(false);
    const [density, setDensity] = useState<"comfortable" | "compact">(() =>
        typeof window !== "undefined" && localStorage.getItem(BACKLOG_DENSITY_KEY) === "compact"
            ? "compact"
            : "comfortable",
    );
    const compact = density === "compact";
    const toggleDensity = () =>
        setDensity((d) => {
            const next = d === "compact" ? "comfortable" : "compact";
            try {
                localStorage.setItem(BACKLOG_DENSITY_KEY, next);
            } catch {
                /* localStorage unavailable — keep the in-memory toggle */
            }
            return next;
        });
    const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
    const toggleGroup = (key: string) =>
        setOpenGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    // #587: search + "where do I start" sort. Search jumps straight to any
    // city/customer/address; sort answers "where do I start" objectively.
    const [query, setQuery] = useState("");
    const [sortMode, setSortMode] = useState<"area" | "urgent" | "az">("area");

    // PRE-CUTOVER — per-card expand-in-place. Keyed by job id, mirrors the
    // openGroups pattern. Comfortable density only; compact stays single-line.
    const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
    const toggleJob = (id: string) =>
        setExpandedJobs((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    // Escape collapses every expanded card.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setExpandedJobs((prev) => (prev.size ? new Set() : prev));
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const [techForGroup, setTechForGroup] = useState<Record<string, string>>({});
    const [pendingGroupKey, setPendingGroupKey] = useState<string | null>(null);
    // PRE-CUTOVER #39 — drop-target highlight while a job is being
    // dragged off the swimlane back onto the backlog. Must be declared
    // before any conditional `return`: React Hooks must run in the same
    // order on every render, and the panel has a collapsed/full branch
    // below. (Previously this lived after the collapsed-mode early
    // return — that was a hooks-rules violation that would crash the
    // panel on collapsed-mode flips.)
    const [dragOver, setDragOver] = useState(false);

    // Shared drag-over / drag-leave / drop wiring for the panel root.
    // Both the collapsed-mode summary card AND the full-mode panel
    // accept drops so unscheduling works regardless of which surface
    // the dispatcher has open.
    const unscheduleDropHandlers = onUnscheduleJob
        ? {
              onDragOver: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOver(true);
              },
              onDragLeave: () => setDragOver(false),
              onDrop: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  setDragOver(false);
                  const jobId = e.dataTransfer.getData("text/plain");
                  if (jobId) onUnscheduleJob(jobId);
              },
          }
        : {};

    const assignGroupMutation = useMutation({
        mutationFn: async (vars: { jobs: ClusterJob[]; techId: string; date: string }) => {
            const dayStart = new Date(`${vars.date}T09:00:00`);
            for (let i = 0; i < vars.jobs.length; i++) {
                const job = vars.jobs[i]!;
                const start = new Date(dayStart.getTime() + i * DEFAULT_VISIT_MINUTES * 60_000);
                const end = new Date(start.getTime() + DEFAULT_VISIT_MINUTES * 60_000);
                try {
                    await api.patch(`/api/jobs/${job.id}`, {
                        assignedTo: vars.techId,
                        scheduledStart: start.toISOString(),
                        scheduledEnd: end.toISOString(),
                        status: "scheduled",
                    });
                } catch {
                    // best-effort bulk; failures will resurface on the next refetch
                }
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
            setPendingGroupKey(null);
        },
        onError: () => setPendingGroupKey(null),
    });

    // Unqueue — take a job out of the Awaiting Dispatch backlog without
    // cancelling it. Sets status to 'unqueued', which the /dispatch/clusters
    // query excludes (see dispatchInsights.ts).
    const unqueueMutation = useMutation({
        mutationFn: (jobId: string) => api.patch(`/api/jobs/${jobId}`, { status: "unqueued" }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            toast.success("Job removed from the dispatch backlog.");
        },
        onError: () => toast.error("Couldn't unqueue the job. Please try again."),
    });

    const { multi: rawMulti, singles: rawSingles } = useMemo(
        () => rebucketByCity(clustersQuery.data?.clusters ?? []),
        [clustersQuery.data],
    );
    const totalJobs = clustersQuery.data?.totalJobs ?? 0;
    const allJobs = useMemo(() => [...rawMulti.flatMap((g) => g.jobs), ...rawSingles], [rawMulti, rawSingles]);

    // ── Search + sort (#587) ────────────────────────────────────────────
    const q = query.trim().toLowerCase();
    const matchesQuery = useCallback(
        (j: ClusterJob) =>
            !q ||
            [j.city, j.customerName, j.siteName, j.siteAddress, j.title, j.jobTypeName].some((v) =>
                v ? v.toLowerCase().includes(q) : false,
            ),
        [q],
    );
    // Multi-city groups, search-filtered, then ordered per the sort control.
    const groupsToRender = useMemo(() => {
        const filtered = rawMulti
            .map((g) => ({ ...g, jobs: g.jobs.filter(matchesQuery) }))
            .filter((g) => g.jobs.length > 0);
        if (sortMode === "az") return filtered.sort((a, b) => a.label.localeCompare(b.label));
        return filtered.sort((a, b) => b.jobs.length - a.jobs.length); // "area"
    }, [rawMulti, matchesQuery, sortMode]);
    const singlesToRender = useMemo(() => rawSingles.filter(matchesQuery), [rawSingles, matchesQuery]);
    // "Most urgent" flattens everything into one deadline-ordered list — the
    // objective "start here" answer when the board is overwhelming.
    const urgentJobs = useMemo(() => allJobs.filter(matchesQuery).slice().sort(byDueDateAsc), [allJobs, matchesQuery]);
    const visibleCount =
        sortMode === "urgent"
            ? urgentJobs.length
            : groupsToRender.reduce((n, g) => n + g.jobs.length, 0) + singlesToRender.length;

    // While searching, matching groups auto-open so results are visible without
    // a second click; otherwise honor the manual expand/collapse state.
    const isGroupOpen = (key: string) => (q ? true : openGroups.has(key));
    const expandAll = () => setOpenGroups(new Set(groupsToRender.map((g) => g.key)));
    const collapseAll = () => setOpenGroups(new Set());

    // Shared right-click menu for any backlog job row (group rows + single
    // stops + the urgent list). Same actions as the detailed group cards.
    const backlogRowMenu = (j: ClusterJob) => (
        <ContextMenuContent className="w-44">
            <ContextMenuItem onSelect={() => navigate(`/jobs/${j.id}`)}>Open job</ContextMenuItem>
            <ContextMenuItem onSelect={() => (onScheduleJob ?? onJobClick)?.(j.id)}>Schedule</ContextMenuItem>
            <ContextMenuItem onSelect={() => navigate(`/jobs/${j.id}/edit`)}>Edit job</ContextMenuItem>
            {onAssignJob && techs.length > 0 && (
                <>
                    <ContextMenuSeparator />
                    <ContextMenuLabel>Assign to tech</ContextMenuLabel>
                    {techs.map((t) => (
                        <ContextMenuItem key={t.id} onSelect={() => onAssignJob(j.id, t.id)}>
                            {`${t.first_name} ${t.last_name}`.trim()}
                        </ContextMenuItem>
                    ))}
                </>
            )}
            <ContextMenuSeparator />
            <ContextMenuItem
                onSelect={() => unqueueMutation.mutate(j.id)}
                title="Hides the job from the dispatch backlog without cancelling it. It stays on the customer/job record."
            >
                Remove from dispatch queue
            </ContextMenuItem>
            {onCancelJob && (
                <ContextMenuItem
                    onSelect={() => onCancelJob(j.id)}
                    className="text-red-600 focus:text-red-600"
                    title="Cancel this job (records a reason and audits the change)."
                >
                    Cancel job…
                </ContextMenuItem>
            )}
        </ContextMenuContent>
    );

    // Compact, CITY-LED, draggable row for single-job cities and the urgent
    // list. #587: the city name leads so a lone stop is findable at a glance —
    // no more digging through an opaque "single-job locations" bucket.
    const renderSingleRow = (j: ClusterJob) => {
        const dueState = classifyDueState(j.dueByDate);
        return (
            <ContextMenu key={j.id}>
                <ContextMenuTrigger asChild>
                    <li
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData("text/plain", j.id);
                            e.dataTransfer.effectAllowed = "move";
                            onJobDragStart?.(j.id);
                        }}
                        onDragEnd={() => onJobDragEnd?.()}
                        className={cn(
                            "group flex cursor-grab items-center gap-2 rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-xs shadow-sm transition-all duration-150",
                            "hover:-translate-y-px hover:border-primary/50 hover:shadow-md active:cursor-grabbing active:translate-y-0",
                            dueState === "overdue" && "border-l-2 border-l-red-500",
                            dueState && dueState !== "overdue" && "border-l-2 border-l-amber-400",
                            showReadiness && TRAY_NOT_READY_ROW,
                        )}
                        title="Drag onto a tech, or use Schedule"
                    >
                        <GripVertical
                            className="h-3.5 w-3.5 shrink-0 text-foreground/40 transition-colors group-hover:text-foreground/80"
                            aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                                <span className="shrink-0 rounded bg-sky-100 px-1 py-0 text-[10px] font-bold text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
                                    {j.city?.trim() || "No city"}
                                </span>
                                <span className="truncate font-medium text-foreground">
                                    {j.customerName || j.siteName || j.title}
                                </span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground/70">
                                <span className="min-w-0 truncate">
                                    {j.jobTypeName || j.title}
                                    {(j.deviceCount ?? 0) > 0
                                        ? ` · ${j.deviceCount} device${j.deviceCount === 1 ? "" : "s"}`
                                        : ""}
                                </span>
                                {j.dueByDate && (
                                    <span
                                        className={cn(
                                            "shrink-0 rounded-full px-1.5 py-0 text-[9px] font-semibold tabular-nums",
                                            dueState === "overdue"
                                                ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
                                                : dueState
                                                  ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                                  : "bg-muted text-foreground/60",
                                        )}
                                    >
                                        {formatDueDatePill(j.dueByDate)}
                                    </span>
                                )}
                            </div>
                            {showReadiness && <TrayReadyTags jobId={j.id} />}
                        </div>
                        {/* #828 M12: no live Schedule on a job with no location —
                            disable-with-reason; the row still opens the job to fix it. */}
                        <button
                            type="button"
                            disabled={!j.city?.trim()}
                            onClick={(e) => {
                                e.stopPropagation();
                                (onScheduleJob ?? onJobClick)?.(j.id);
                            }}
                            title={
                                j.city?.trim()
                                    ? undefined
                                    : "Choose a location first — this job has no address to route to. Open it to fix the site."
                            }
                            className="shrink-0 rounded-md border border-primary/60 px-2 py-0.5 text-[11px] font-semibold text-primary transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent"
                        >
                            Schedule
                        </button>
                    </li>
                </ContextMenuTrigger>
                {backlogRowMenu(j)}
            </ContextMenu>
        );
    };

    const humanReview = useMemo(
        () =>
            diagnoseHumanReview(allJobs, {
                techSkills,
                jobSkillReqs,
                techCityCerts,
                jobCityRestriction,
            }),
        [allJobs, techSkills, jobSkillReqs, techCityCerts, jobCityRestriction],
    );

    /* ─────────── COLLAPSED MODE (auto-scheduling is ON) ───────────
       H8: only collapse to the summary card when there is nothing for a human
       to act on. As soon as jobs are awaiting dispatch we keep the full backlog
       (cards + drag hints) visible so the dispatcher can override the
       auto-scheduler without first hunting for a "Show full backlog" button.
       The full-mode header still shows the auto-scheduler-aware tip. */
    if (collapsed && !forceExpanded && totalJobs === 0) {
        return (
            <div
                className={cn(
                    "rounded-lg border bg-card p-3 shadow-sm transition-colors",
                    dragOver && "ring-2 ring-primary bg-primary/5",
                )}
                {...unscheduleDropHandlers}
            >
                <div className="flex items-center gap-2">
                    <div className="rounded-md bg-emerald-100 p-1.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                        <Sparkles className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold leading-tight">Auto-scheduler is handling the queue</p>
                        <p className="text-[11px] text-foreground/80">
                            {clustersQuery.isLoading
                                ? "Loading…"
                                : totalJobs === 0
                                  ? "Nothing awaiting dispatch."
                                  : `${totalJobs} job${totalJobs === 1 ? "" : "s"} awaiting dispatch — queued for the next pass.`}
                        </p>
                        {onUnscheduleJob && (
                            <p className="mt-0.5 text-[10px] text-foreground/70">Drop a job here to unschedule.</p>
                        )}
                    </div>
                </div>

                {humanReview.length > 0 && (
                    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] dark:border-amber-900/60 dark:bg-amber-900/20">
                        <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-200">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {humanReview.length} need
                            {humanReview.length === 1 ? "s" : ""} a human
                        </div>
                        <ul className="mt-1 space-y-0.5 text-amber-800/90 dark:text-amber-200/80">
                            {humanReview.slice(0, 3).map((entry) => (
                                <li key={entry.job.id} className="truncate">
                                    • {entry.job.title} — {entry.reasons[0]}
                                </li>
                            ))}
                            {humanReview.length > 3 && <li>+ {humanReview.length - 3} more</li>}
                        </ul>
                    </div>
                )}

                <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 h-7 w-full text-xs"
                    onClick={() => setForceExpanded(true)}
                >
                    Show full backlog
                    <ChevronDown className="ml-1 h-3.5 w-3.5" />
                </Button>
            </div>
        );
    }

    /* ─────────── FULL MODE (auto-scheduling is OFF, or user expanded) ─────────── */

    return (
        <div
            className={cn(
                "rounded-lg border bg-muted/30 p-3 transition-colors",
                dragOver && "ring-2 ring-primary bg-primary/5",
            )}
            {...unscheduleDropHandlers}
        >
            {/* PRE-CUTOVER #40 — clearer titling + subtitle.
                Old header was just "Backlog" + a count badge, and the
                dispatcher would ask "backlog of what?". Spelling out
                "Awaiting Dispatch" + a one-line description makes the
                intent obvious and primes the city groupings below. */}
            <div className="mb-1 flex items-center gap-2">
                <div className="rounded-md bg-amber-100 p-1 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                    <Inbox className="h-3.5 w-3.5" />
                </div>
                <h3 className="text-base font-semibold leading-none">Awaiting Dispatch</h3>
                <Badge
                    variant="secondary"
                    className="ml-auto"
                    title={q ? `${visibleCount} match${visibleCount === 1 ? "" : "es"} of ${totalJobs}` : undefined}
                >
                    {q ? `${visibleCount}/${totalJobs}` : totalJobs}
                </Badge>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={toggleDensity}
                    title={compact ? "Switch to comfortable cards" : "Switch to compact cards"}
                >
                    {compact ? "Comfortable" : "Compact"}
                </Button>
                {collapsed && forceExpanded && totalJobs === 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setForceExpanded(false)}
                        title="Collapse back to summary"
                    >
                        Collapse
                    </Button>
                )}
            </div>
            <p className="mb-2 text-[11px] text-foreground/80">
                Jobs waiting to be placed on a technician's day. Grouped by city so you can bulk-assign nearby work in
                one drag.
            </p>
            <div className="mb-3 flex items-center gap-1.5 rounded-md border border-dashed border-foreground/20 px-2 py-1 text-[10px] text-foreground/80">
                <GripVertical className="h-3 w-3" />
                <span>Drag a job onto a tech to schedule it · drop one back here to unschedule</span>
            </div>
            {/* AUTOMATION PRIVACY (THE-GRID-REDESIGN §8): the auto-scheduler
                sentence is an automation hint — dispatch.manage holders only.
                Everyone else gets the manual tip in both modes. */}
            <p className="mb-2 text-[11px] text-foreground/70">
                {collapsed && canAutomate
                    ? "Auto-scheduler runs on the next tick — drop a job on a tech now to override."
                    : "Tip: open a group below to bulk-assign all of its jobs to one tech."}
            </p>

            {/* #587: search jumps straight to any city/customer/address. */}
            <div className="mb-2 flex items-center gap-1.5 rounded-md border bg-background px-2 py-1">
                <Search className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search city, customer, address…"
                    className="h-6 w-full min-w-0 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
                    aria-label="Search awaiting-dispatch jobs"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="shrink-0 rounded p-0.5 text-foreground/50 hover:bg-muted hover:text-foreground"
                        title="Clear search"
                        aria-label="Clear search"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>

            {/* #587: "where do I start" — sort + expand controls. */}
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Start with
                </span>
                <div className="inline-flex rounded-md border bg-background p-0.5 text-[11px]">
                    {(
                        [
                            ["area", "By area"],
                            ["urgent", "Most urgent"],
                            ["az", "A–Z"],
                        ] as const
                    ).map(([mode, label]) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => setSortMode(mode)}
                            className={cn(
                                "rounded px-2 py-0.5 font-medium transition",
                                sortMode === mode
                                    ? "bg-primary text-primary-foreground"
                                    : "text-foreground/70 hover:bg-muted",
                            )}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {sortMode !== "urgent" && groupsToRender.length > 0 && (
                    <div className="ml-auto flex items-center gap-2 text-[11px]">
                        <button type="button" onClick={expandAll} className="font-medium text-primary hover:underline">
                            Expand all
                        </button>
                        <span className="text-foreground/30">·</span>
                        <button
                            type="button"
                            onClick={collapseAll}
                            className="font-medium text-primary hover:underline"
                        >
                            Collapse all
                        </button>
                    </div>
                )}
            </div>

            {humanReview.length > 0 && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] dark:border-amber-900/60 dark:bg-amber-900/20">
                    <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Needs human review
                        <Badge variant="outline" className="ml-1 h-4 border-amber-300 px-1 text-[10px]">
                            {humanReview.length}
                        </Badge>
                    </div>
                    {/* Quick explanation of what landed in this bucket
                        so the dispatcher knows whether to assign a
                        certified tech, add a new city cert, or push
                        back to the customer. */}
                    <p className="mt-1 text-[10px] leading-snug text-amber-800/90 dark:text-amber-200/80">
                        No on-staff tech holds the required skill, or no tech is certified for the job's restricted
                        city. The auto-scheduler won't pick these up — drag onto a qualifying tech yourself or update
                        certifications.
                    </p>
                </div>
            )}

            {clustersQuery.isLoading ? (
                <PageSpinner />
            ) : totalJobs === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                    <p className="text-sm font-medium">Nothing awaiting dispatch.</p>
                    <p className="text-xs text-foreground/80">Every job has a tech + a time. Nice work.</p>
                </div>
            ) : visibleCount === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Search className="h-7 w-7 text-foreground/40" />
                    <p className="text-sm font-medium">No matches for “{query}”.</p>
                    <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="text-xs font-medium text-primary hover:underline"
                    >
                        Clear search
                    </button>
                </div>
            ) : sortMode === "urgent" ? (
                <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
                    <div className="flex items-center gap-1.5 px-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/60">
                        <AlertTriangle className="h-3 w-3" />
                        Most urgent first · {urgentJobs.length}
                    </div>
                    <ul className="space-y-1">{urgentJobs.map((j) => renderSingleRow(j))}</ul>
                </div>
            ) : (
                <div className="max-h-[60vh] space-y-1.5 overflow-y-auto pr-1">
                    {/* PRE-CUTOVER #40 — section label so the city
                        groups read as "By location" instead of a
                        bare list of city names. */}
                    {groupsToRender.length > 0 && (
                        <div className="flex items-center gap-1.5 px-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/60">
                            <MapPin className="h-3 w-3" />
                            By location
                        </div>
                    )}
                    {groupsToRender.map((group) => {
                        const open = isGroupOpen(group.key);
                        const isPending = pendingGroupKey === group.key && assignGroupMutation.isPending;
                        const selectedTechId = techForGroup[group.key] ?? "";
                        const flaggedIds = new Set(
                            humanReview.filter((r) => group.jobs.some((j) => j.id === r.job.id)).map((r) => r.job.id),
                        );
                        return (
                            <div key={group.key} className="rounded-md border bg-background">
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.key)}
                                    className="sticky top-0 z-10 flex w-full items-center justify-between gap-2 rounded-t-md border-b border-transparent bg-background/95 px-3 py-2.5 text-left backdrop-blur transition hover:bg-muted/50"
                                >
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                        {open ? (
                                            <ChevronDown className="h-4 w-4 shrink-0 text-foreground/80" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 shrink-0 text-foreground/80" />
                                        )}
                                        {group.isRollup ? (
                                            <div className="rounded bg-muted p-1 text-foreground/80">
                                                <Inbox className="h-3.5 w-3.5" />
                                            </div>
                                        ) : (
                                            <MapPin className="h-4 w-4 shrink-0 text-foreground/80" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 truncate text-sm font-semibold">
                                                {group.label}
                                            </div>
                                            {group.jurisdiction && !group.isRollup && (
                                                <div className="mt-0.5 truncate text-[11px] text-foreground/80">
                                                    {group.jurisdiction}
                                                </div>
                                            )}
                                            {/* G6.1 polish: list the member cities so the
                                                singles rollup scans before opening. */}
                                            {group.isRollup && (group.cities?.length ?? 0) > 0 && (
                                                <div
                                                    className="mt-0.5 truncate text-[11px] font-medium text-foreground/80"
                                                    title={group.cities!.join(" · ")}
                                                >
                                                    {group.cities!.join(" · ")}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* #629: draining a city gives visible feedback — jobs
                                        from this city already on the board (today or later)
                                        vs. what's still waiting here. Only shows once
                                        something has actually been placed. */}
                                    {!group.isRollup && group.scheduledCount > 0 && (
                                        <span
                                            className="inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-full bg-emerald-100 px-1.5 text-[10px] font-semibold tabular-nums text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                                            title={`${group.scheduledCount} of ${group.scheduledCount + group.jobs.length} ${group.label} jobs are already placed on the board (today or later) — ${group.jobs.length} still to place`}
                                            data-testid="placed-progress"
                                        >
                                            {group.scheduledCount} of {group.scheduledCount + group.jobs.length} placed
                                        </span>
                                    )}
                                    {flaggedIds.size > 0 && (
                                        <span
                                            className="inline-flex h-5 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                            title={`${flaggedIds.size} need a human`}
                                        >
                                            <AlertTriangle className="h-3 w-3" />
                                            {flaggedIds.size}
                                        </span>
                                    )}
                                    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-foreground/10 px-1.5 text-xs font-semibold tabular-nums text-foreground">
                                        {group.jobs.length}
                                    </span>
                                </button>

                                {open && (
                                    <div className="border-t bg-muted/30 px-3 py-2">
                                        <ul className="mb-2 space-y-1">
                                            {group.jobs.map((j) => {
                                                const dueState = classifyDueState(j.dueByDate);
                                                // Past-due and "due soon" jobs are flagged
                                                // even when no human-review reason was raised
                                                // — the deadline itself is the warning. The
                                                // existing tech-skill / city-cert flags still
                                                // apply on top.
                                                const flagged = flaggedIds.has(j.id) || dueState !== null;
                                                return (
                                                    <ContextMenu key={j.id}>
                                                        <ContextMenuTrigger asChild>
                                                            <li
                                                                draggable
                                                                onDragStart={(e) => {
                                                                    e.dataTransfer.setData("text/plain", j.id);
                                                                    e.dataTransfer.effectAllowed = "move";
                                                                    onJobDragStart?.(j.id);
                                                                }}
                                                                onDragEnd={() => onJobDragEnd?.()}
                                                                className={cn(
                                                                    // Draggable tile with a grip handle,
                                                                    // hover lift, and primary-tint border
                                                                    // so it reads as "grab me". The card
                                                                    // body click expands it in place.
                                                                    "group flex cursor-grab flex-col gap-1 rounded-md border border-border/60 bg-card px-2.5 py-2 text-xs shadow-sm transition-all duration-150",
                                                                    "hover:-translate-y-px hover:border-primary/50 hover:shadow-md",
                                                                    "active:cursor-grabbing active:translate-y-0 active:shadow-sm",
                                                                    flagged && "border-l-2 border-l-amber-400",
                                                                    dueState === "overdue" &&
                                                                        "border-l-2 border-l-red-500",
                                                                    showReadiness && TRAY_NOT_READY_ROW,
                                                                )}
                                                                title="Drag onto a tech, click to expand, or use the calendar to schedule"
                                                            >
                                                                <div className="flex flex-wrap items-start gap-2">
                                                                    <GripVertical
                                                                        className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-grab text-foreground/40 transition-colors group-hover:text-foreground/80"
                                                                        aria-hidden="true"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                    {/* Card body — clicking toggles
                                                                        expand (comfortable density only). */}
                                                                    <div
                                                                        role={compact ? undefined : "button"}
                                                                        tabIndex={compact ? undefined : 0}
                                                                        onClick={
                                                                            compact ? undefined : () => toggleJob(j.id)
                                                                        }
                                                                        onKeyDown={
                                                                            compact
                                                                                ? undefined
                                                                                : (e) => {
                                                                                      if (
                                                                                          e.key === "Enter" ||
                                                                                          e.key === " "
                                                                                      ) {
                                                                                          e.preventDefault();
                                                                                          toggleJob(j.id);
                                                                                      }
                                                                                  }
                                                                        }
                                                                        className={cn(
                                                                            "flex min-w-[8rem] flex-1 flex-col gap-0.5 text-left",
                                                                            !compact && "cursor-pointer",
                                                                        )}
                                                                    >
                                                                        {compact ? (
                                                                            <span className="truncate text-[11px] text-foreground">
                                                                                {/* City leads, bold — Corbin: "the
                                                                                    cities hide in there". */}
                                                                                {j.city?.trim() && (
                                                                                    <span className="font-bold">
                                                                                        {j.city.trim()}
                                                                                        {" · "}
                                                                                    </span>
                                                                                )}
                                                                                <span className="font-semibold">
                                                                                    {j.siteName ??
                                                                                        j.customerName ??
                                                                                        j.title}
                                                                                </span>
                                                                                {j.jobTypeName && (
                                                                                    <span className="text-foreground/70">
                                                                                        {" · "}
                                                                                        {j.jobTypeName}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                {/* Line 1 — CITY chip leads (Corbin:
                                                                                    cities + job type are crucial for
                                                                                    planning), then the site name. */}
                                                                                <span className="flex min-w-0 flex-wrap items-center gap-1">
                                                                                    <span className="inline-flex shrink-0 items-center rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-900 dark:bg-sky-900/50 dark:text-sky-100">
                                                                                        {j.city?.trim() || "No city"}
                                                                                    </span>
                                                                                    <span
                                                                                        className="min-w-0 break-words text-[12px] font-semibold leading-tight text-foreground"
                                                                                        title={
                                                                                            j.siteName ??
                                                                                            j.customerName ??
                                                                                            j.title
                                                                                        }
                                                                                    >
                                                                                        {j.siteName ??
                                                                                            j.customerName ??
                                                                                            j.title}
                                                                                    </span>
                                                                                </span>
                                                                                {/* Line 2 — JOB TYPE + device count
                                                                                    + due chip, full strength. */}
                                                                                <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-medium text-foreground">
                                                                                    <span className="min-w-0 break-words">
                                                                                        {j.jobTypeName ?? j.title}
                                                                                    </span>
                                                                                    {(j.deviceCount ?? 0) > 0 && (
                                                                                        <span className="shrink-0 text-foreground/70">
                                                                                            {`· ${j.deviceCount} device${
                                                                                                j.deviceCount === 1
                                                                                                    ? ""
                                                                                                    : "s"
                                                                                            }`}
                                                                                        </span>
                                                                                    )}
                                                                                    {j.dueByDate && (
                                                                                        <span
                                                                                            className={cn(
                                                                                                "inline-flex w-fit shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                                                                                                dueTierClass(
                                                                                                    backlogDueTier(
                                                                                                        j.dueByDate,
                                                                                                    ),
                                                                                                ),
                                                                                            )}
                                                                                            title={`Due ${j.dueByDate}`}
                                                                                        >
                                                                                            {formatDueDatePill(
                                                                                                j.dueByDate,
                                                                                            )}
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                                {/* Line 3 — customer name */}
                                                                                {j.customerName && (
                                                                                    <span className="truncate text-[10px] text-foreground/70">
                                                                                        {j.customerName}
                                                                                    </span>
                                                                                )}
                                                                                {/* Line 4 — full mailing address incl.
                                                                                    ZIP, large + bold so it reads at a
                                                                                    glance (Corbin: cards must show the
                                                                                    whole address, not a tiny street). */}
                                                                                {(() => {
                                                                                    const fullAddress =
                                                                                        formatJobAddress({
                                                                                            addressLine: j.siteAddress,
                                                                                            city: j.city,
                                                                                            region: j.region,
                                                                                            postalCode: j.postalCode,
                                                                                        });
                                                                                    if (!fullAddress) return null;
                                                                                    return (
                                                                                        <span className="flex items-start gap-1 text-[12px] font-semibold leading-snug text-foreground">
                                                                                            <MapPin
                                                                                                className="mt-0.5 h-3 w-3 shrink-0 text-foreground/50"
                                                                                                aria-hidden="true"
                                                                                            />
                                                                                            <span className="min-w-0 break-words">
                                                                                                {fullAddress}
                                                                                            </span>
                                                                                        </span>
                                                                                    );
                                                                                })()}
                                                                                {/* Return visit story: reason,
                                                                                    remaining devices, original
                                                                                    visit — the trip's context
                                                                                    survives the reschedule. */}
                                                                                {j.returnContext && (
                                                                                    <ReturnVisitChip
                                                                                        context={j.returnContext}
                                                                                        jobId={j.id}
                                                                                    />
                                                                                )}
                                                                            </>
                                                                        )}
                                                                        {showReadiness && (
                                                                            <TrayReadyTags jobId={j.id} />
                                                                        )}
                                                                    </div>
                                                                    {/* Trailing actions + status chips grouped so
                                                                        they wrap to their own line as a unit when the
                                                                        card is narrow — keeps the title from being
                                                                        starved to a single-character-wide column. */}
                                                                    <div className="flex shrink-0 flex-wrap items-start justify-end gap-1">
                                                                        {/* H9: a labeled Schedule button (not an icon
                                                                        alone) so the no-drag path is discoverable to
                                                                        dispatchers coming from ServiceTitan. */}
                                                                        <button
                                                                            type="button"
                                                                            draggable={false}
                                                                            disabled={!j.city?.trim()}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                (onScheduleJob ?? onJobClick)?.(j.id);
                                                                            }}
                                                                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-1.5 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:text-muted-foreground"
                                                                            title={
                                                                                j.city?.trim()
                                                                                    ? "Schedule this job"
                                                                                    : "Choose a location first — this job has no address to route to. Open it to fix the site."
                                                                            }
                                                                            aria-label="Schedule this job"
                                                                        >
                                                                            <CalendarPlus className="h-3.5 w-3.5" />
                                                                            Schedule
                                                                        </button>
                                                                        {isContactStale(
                                                                            j.siteContactLastVerifiedAt,
                                                                        ) && (
                                                                            <span
                                                                                className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                                                                title={
                                                                                    j.siteContactLastVerifiedAt
                                                                                        ? "Site contact not confirmed in over a year — verify before dispatching"
                                                                                        : "Site contact never verified — confirm before dispatching"
                                                                                }
                                                                            >
                                                                                <UserX className="h-2.5 w-2.5" />
                                                                                Contact
                                                                            </span>
                                                                        )}
                                                                        {j.partsBlocked ? (
                                                                            <span
                                                                                className="shrink-0 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800 dark:bg-orange-900/40 dark:text-orange-200"
                                                                                title="Parts not received — blocked from dispatch until the PO is received"
                                                                            >
                                                                                Parts
                                                                            </span>
                                                                        ) : j.earliestDispatchDate ? (
                                                                            <span
                                                                                className="shrink-0 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                                                                                title={`Parts en route — not before ${j.earliestDispatchDate}`}
                                                                            >
                                                                                {`≥ ${formatNotBeforeLabel(
                                                                                    j.earliestDispatchDate,
                                                                                )}`}
                                                                            </span>
                                                                        ) : null}
                                                                        {flagged && (
                                                                            <AlertTriangle
                                                                                className="mt-0.5 h-3 w-3 shrink-0 text-amber-500"
                                                                                aria-label="Needs human review"
                                                                            />
                                                                        )}
                                                                        {!compact &&
                                                                            (expandedJobs.has(j.id) ? (
                                                                                <ChevronDown
                                                                                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/40 transition-colors group-hover:text-foreground/70"
                                                                                    aria-hidden="true"
                                                                                />
                                                                            ) : (
                                                                                <ChevronRight
                                                                                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/30 transition-colors group-hover:text-foreground/60"
                                                                                    aria-hidden="true"
                                                                                />
                                                                            ))}
                                                                    </div>
                                                                </div>

                                                                {/* Expanded detail (comfortable only). */}
                                                                {!compact && expandedJobs.has(j.id) && (
                                                                    <div
                                                                        className="mt-1 space-y-1.5 border-t pt-2 text-[11px]"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div>
                                                                            <span className="font-semibold text-foreground/70">
                                                                                Notes
                                                                            </span>
                                                                            <p className="mt-0.5 whitespace-pre-wrap break-words text-foreground/80">
                                                                                {j.notes && j.notes.trim() ? (
                                                                                    j.notes
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">
                                                                                        No notes.
                                                                                    </span>
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                        {j.waterProviderName && (
                                                                            <div className="text-foreground/70">
                                                                                <span className="font-semibold">
                                                                                    Water provider:
                                                                                </span>{" "}
                                                                                {j.waterProviderName}
                                                                            </div>
                                                                        )}
                                                                        {j.waterAccountNumber && (
                                                                            <div className="text-foreground/70">
                                                                                <span className="font-semibold">
                                                                                    Account #:
                                                                                </span>{" "}
                                                                                {j.waterAccountNumber}
                                                                            </div>
                                                                        )}
                                                                        <div className="flex items-center gap-2 pt-0.5">
                                                                            <Button
                                                                                size="sm"
                                                                                className="h-7 text-xs"
                                                                                draggable={false}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    (onScheduleJob ?? onJobClick)?.(
                                                                                        j.id,
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <CalendarPlus className="mr-1 h-3.5 w-3.5" />
                                                                                Schedule
                                                                            </Button>
                                                                            {/* G5: same-tab; real href keeps
                                                                                cmd/ctrl+click for a new tab. */}
                                                                            <Link
                                                                                to={`/jobs/${j.id}`}
                                                                                draggable={false}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                className="text-xs font-medium text-primary hover:underline"
                                                                            >
                                                                                Open job →
                                                                            </Link>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        </ContextMenuTrigger>
                                                        <ContextMenuContent className="w-44">
                                                            <ContextMenuItem onSelect={() => navigate(`/jobs/${j.id}`)}>
                                                                Open job
                                                            </ContextMenuItem>
                                                            <ContextMenuItem
                                                                onSelect={() => (onScheduleJob ?? onJobClick)?.(j.id)}
                                                            >
                                                                Schedule
                                                            </ContextMenuItem>
                                                            <ContextMenuItem
                                                                onSelect={() => navigate(`/jobs/${j.id}/edit`)}
                                                            >
                                                                Edit job
                                                            </ContextMenuItem>
                                                            {onAssignJob && techs.length > 0 && (
                                                                <>
                                                                    <ContextMenuSeparator />
                                                                    <ContextMenuLabel>Assign to tech</ContextMenuLabel>
                                                                    {techs.map((t) => (
                                                                        <ContextMenuItem
                                                                            key={t.id}
                                                                            onSelect={() => onAssignJob(j.id, t.id)}
                                                                        >
                                                                            {`${t.first_name} ${t.last_name}`.trim()}
                                                                        </ContextMenuItem>
                                                                    ))}
                                                                </>
                                                            )}
                                                            <ContextMenuSeparator />
                                                            <ContextMenuItem
                                                                onSelect={() => unqueueMutation.mutate(j.id)}
                                                                title="Hides the job from the dispatch backlog without cancelling it. It stays on the customer/job record."
                                                            >
                                                                Remove from dispatch queue
                                                            </ContextMenuItem>
                                                            {onCancelJob && (
                                                                <ContextMenuItem
                                                                    onSelect={() => onCancelJob(j.id)}
                                                                    className="text-red-600 focus:text-red-600"
                                                                    title="Cancel this job (records a reason and audits the change)."
                                                                >
                                                                    Cancel job…
                                                                </ContextMenuItem>
                                                            )}
                                                        </ContextMenuContent>
                                                    </ContextMenu>
                                                );
                                            })}
                                        </ul>
                                        {!group.isRollup && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <select
                                                    value={selectedTechId}
                                                    onChange={(e) =>
                                                        setTechForGroup((prev) => ({
                                                            ...prev,
                                                            [group.key]: e.target.value,
                                                        }))
                                                    }
                                                    className="h-7 flex-1 rounded-md border bg-background px-2 text-xs"
                                                >
                                                    <option value="">Bulk assign tech…</option>
                                                    {techs.map((t) => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.first_name} {t.last_name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <Button
                                                    size="sm"
                                                    className="h-7 text-xs"
                                                    disabled={!selectedTechId || isPending}
                                                    onClick={() => {
                                                        if (!selectedTechId) return;
                                                        setPendingGroupKey(group.key);
                                                        assignGroupMutation.mutate({
                                                            jobs: group.jobs,
                                                            techId: selectedTechId,
                                                            date: targetDate,
                                                        });
                                                    }}
                                                >
                                                    {isPending ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        "Assign all"
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {/* #587: single-job cities stay NAMED and findable — a flat,
                        alphabetical, draggable list instead of an opaque bucket
                        you had to open and dig through. */}
                    {singlesToRender.length > 0 && (
                        <div className="pt-1">
                            <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-background/95 px-0.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground/60 backdrop-blur">
                                <MapPin className="h-3 w-3" />
                                Single stops · {singlesToRender.length}
                            </div>
                            <ul className="space-y-1">{singlesToRender.map((j) => renderSingleRow(j))}</ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
